// UnityBridge.ts — a small observable singleton so the editor panel (left) can talk to the Unity
// instance owned by the game view (right). GameView registers/clears the live instance; EditorPanel
// sends the generated level to it. Same pattern as the dashboard's observable service singletons.
//
// The channel runs BOTH WAYS now. Out: SendMessage, as before. Back: the player has no way to call
// us except through an Emscripten plugin, so Assets/Plugins/WebGL/TwistFileBridge.jslib calls two
// hooks this module installs on `window` — one delivering a finished FILE (the wall PNG), one
// reporting a failure. Installed at import time, before the player is ever created, so a file can
// never arrive before someone is listening.
// The in-scene GameObject + the methods it answers to (see LevelEditorBridge.cs).
const BRIDGE_OBJECT = "LevelEditorBridge";
const LOAD_METHOD = "LoadGeneratedLevel";
const EXPORT_WALL_METHOD = "ExportWallPng";
const RENDER_IMAGE_METHOD = "RenderLevelImage";
const LEVEL_IMAGE_TIMEOUT_MS = 30000;
/**
 * The game's native canvas: 18:39 at exactly 100 px per world unit (the camera is orthographic
 * with size 14.09, and 2818 / (2 x 14.09) = 100). Exports default to it so a wall PNG drops onto
 * a full-size screen with every island where the player sees it.
 */
export const NATIVE_EXPORT_WIDTH = 1302;
export const NATIVE_EXPORT_HEIGHT = 2818;
/**
 * Level images: the whole game screen at the game's native canvas, 1302 x 2818 (18:39). While they are
 * taken the player's backbuffer is pinned to exactly that size (holdBackbuffer), so the picture is the
 * game drawn at full resolution, not the preview pane scaled up.
 */
export const LEVEL_IMAGE_WIDTH = NATIVE_EXPORT_WIDTH;
export const LEVEL_IMAGE_HEIGHT = NATIVE_EXPORT_HEIGHT;
class UnityBridge {
    constructor() {
        this.instance = null;
        this.listeners = new Set();
        // Called by GameView when the player finishes loading (and null on teardown).
        this.setInstance = (instance) => {
            this.instance = instance;
            this.bridgeAnswered = false; // a new player boots its scene all over again
            this.listeners.forEach((fn) => fn());
        };
        // Set once the in-scene LevelEditorBridge has answered a ping (waitUntilReady).
        this.bridgeAnswered = false;
        /**
         * Resolves once the game can take requests: the player is loaded AND its LevelEditorBridge answers.
         *
         * ⚠ The player being loaded (isReady) is not enough. createUnityInstance resolves while the game is
         * still booting through its splash into the Content Editor scene, and only that scene holds the
         * LevelEditorBridge object; a SendMessage to it before then is dropped without a word, so the
         * first request after a page load could simply never be answered. The ping is the wall export
         * with a size that is not one ("ping"): the bridge refuses it at once with a "wall-png" error,
         * which is the answer we wait for — no game change needed, nothing is drawn.
         */
        this.waitUntilReady = async (timeoutMs = 90000) => {
            const deadline = Date.now() + timeoutMs;
            while (!this.instance) {
                if (Date.now() > deadline) {
                    throw new Error("The game did not load in time — reload the page and try again.");
                }
                await new Promise((r) => window.setTimeout(r, 250));
            }
            while (!this.bridgeAnswered) {
                const instance = this.instance;
                if (!instance) {
                    throw new Error("The game was closed while it was loading.");
                }
                const answered = await new Promise((resolve) => {
                    const off = this.onError((kind) => {
                        if (kind === "wall-png") {
                            off();
                            window.clearTimeout(timer);
                            resolve(true);
                        }
                    });
                    const timer = window.setTimeout(() => {
                        off();
                        resolve(false);
                    }, 500);
                    instance.SendMessage(BRIDGE_OBJECT, EXPORT_WALL_METHOD, "ping");
                });
                if (answered) {
                    this.bridgeAnswered = true;
                }
                else if (Date.now() > deadline) {
                    throw new Error("The game did not finish loading in time — reload the page and try again.");
                }
            }
        };
        // Sends a generated level (JSON) to the bridge to load + play it. Returns false if the game isn't
        // ready yet. The bridge survives scene loads (see LevelEditorBridge.cs), so this also works while a
        // level is already running — the game reloads gameplay onto the new board.
        this.playLevel = (levelJson) => {
            if (!this.instance) {
                return false;
            }
            this.instance.SendMessage(BRIDGE_OBJECT, LOAD_METHOD, levelJson);
            return true;
        };
        /**
         * Asks the player for this level's walls as ONE transparent PNG at the given size — every
         * island, framed exactly as the game camera frames them. The picture arrives later through
         * `onFile`; a refusal (no level loaded, no wall) through `onError`. Returns false only when the
         * game is not loaded yet, which is the one failure this side can see for itself.
         */
        this.exportWallPng = (width = NATIVE_EXPORT_WIDTH, height = NATIVE_EXPORT_HEIGHT) => {
            if (!this.instance) {
                return false;
            }
            this.instance.SendMessage(BRIDGE_OBJECT, EXPORT_WALL_METHOD, `${width}x${height}`);
            return true;
        };
        /**
         * Pins the player's backbuffer to width x height until the returned release is called: the canvas
         * keeps its on-screen size (CSS) but the game renders — and Screen.width/height report — exactly
         * this resolution. Releasing hands sizing back to the player, which re-matches the canvas on its
         * next frame.
         */
        this.holdBackbuffer = (width, height) => {
            const module = this.instance?.Module;
            const canvas = module?.canvas;
            if (!module || !canvas) {
                return () => { };
            }
            const previous = module.matchWebGLToCanvasSize;
            module.matchWebGLToCanvasSize = false;
            canvas.width = width;
            canvas.height = height;
            return () => {
                module.matchWebGLToCanvasSize = previous ?? true;
            };
        };
        /**
         * The wall export as a promise: the CURRENT level's walls (exportWallPng), resolved with the PNG's
         * bytes. Used right after renderLevelImage, which leaves the level loaded, so one load gives both
         * the screenshot and the walls. The answer is matched by its file name / error kind ("wall-png");
         * the player runs one export at a time, so there is never more than one to match.
         */
        this.exportWallPngAsync = (width = NATIVE_EXPORT_WIDTH, height = NATIVE_EXPORT_HEIGHT) => {
            if (!this.instance) {
                return Promise.reject(new Error("The game is still loading."));
            }
            return new Promise((resolve, reject) => {
                const finish = () => {
                    offFile();
                    offError();
                    window.clearTimeout(timer);
                };
                const offFile = this.onFile((file) => {
                    if (file.name === `walls-${width}x${height}.png`) {
                        finish();
                        resolve(file.bytes.slice());
                    }
                });
                const offError = this.onError((kind, message) => {
                    if (kind === "wall-png") {
                        finish();
                        reject(new Error(message));
                    }
                });
                const timer = window.setTimeout(() => {
                    finish();
                    reject(new Error("The game did not return the walls in time."));
                }, LEVEL_IMAGE_TIMEOUT_MS);
                this.exportWallPng(width, height);
            });
        };
        this.imageRequests = 0;
        /**
         * Loads `levelJson` in the player and photographs the screen once the board has settled
         * (LevelEditorBridge.RenderLevelImage). Resolves with the PNG's bytes. The request carries an id
         * that comes back in the file name / error kind, so overlapping listeners never take each other's
         * answers. The player leaves the level loaded afterwards.
         */
        this.renderLevelImage = (levelJson, width = LEVEL_IMAGE_WIDTH, height = LEVEL_IMAGE_HEIGHT) => {
            const instance = this.instance;
            if (!instance) {
                return Promise.reject(new Error("The game is still loading."));
            }
            const id = `${Date.now().toString(36)}${(++this.imageRequests).toString(36)}`;
            return new Promise((resolve, reject) => {
                const finish = () => {
                    offFile();
                    offError();
                    window.clearTimeout(timer);
                };
                const offFile = this.onFile((file) => {
                    if (file.name === `level-image-${id}.png`) {
                        finish();
                        resolve(file.bytes.slice());
                    }
                });
                const offError = this.onError((kind, message) => {
                    if (kind === `level-image:${id}`) {
                        finish();
                        reject(new Error(message));
                    }
                });
                const timer = window.setTimeout(() => {
                    finish();
                    reject(new Error("The game did not return the image in time."));
                }, LEVEL_IMAGE_TIMEOUT_MS);
                instance.SendMessage(BRIDGE_OBJECT, RENDER_IMAGE_METHOD, `${id} ${width}x${height}\n${levelJson}`);
            });
        };
        this.subscribe = (fn) => {
            this.listeners.add(fn);
            return () => {
                this.listeners.delete(fn);
            };
        };
        // --- The way back (see the header) ------------------------------------------------------
        this.fileListeners = new Set();
        this.errorListeners = new Set();
        /** Called for every file the player delivers. */
        this.onFile = (fn) => {
            this.fileListeners.add(fn);
            return () => {
                this.fileListeners.delete(fn);
            };
        };
        /** Called for every failure the player reports — show it, or nobody ever learns of it. */
        this.onError = (fn) => {
            this.errorListeners.add(fn);
            return () => {
                this.errorListeners.delete(fn);
            };
        };
        /** @internal the jslib's entry point. */
        this._deliverFile = (name, mime, bytes) => {
            this.fileListeners.forEach((fn) => fn({ name, mime, bytes }));
        };
        /** @internal the jslib's entry point. */
        this._deliverError = (kind, message) => {
            if (this.errorListeners.size === 0) {
                console.error(`[Twist] ${kind}: ${message}`);
                return;
            }
            this.errorListeners.forEach((fn) => fn(kind, message));
        };
    }
    // True once the game is loaded and can receive messages.
    get isReady() {
        return this.instance != null;
    }
}
export const unityBridge = new UnityBridge();
// The plugin's hooks. Installed once at import, before GameView ever builds the player.
window.TwistOnUnityFile = (name, mime, bytes) => unityBridge._deliverFile(name, mime, bytes);
window.TwistOnUnityError = (kind, message) => unityBridge._deliverError(kind, message);
/**
 * Saves bytes the player handed back. A Blob URL rather than a data: URI — these files run to
 * megabytes and a data: URI of that size is a needless copy through a string — and the object URL
 * is revoked on a timer, since the click is asynchronous and revoking it immediately can beat the
 * download to it.
 */
export function downloadBytes(file, nameOverride) {
    // `.slice()` copies the view into a buffer of its own — the bytes came out of the WASM heap,
    // and a Blob over a live view of it would be a Blob over memory Unity keeps using.
    const blob = new Blob([file.bytes.slice().buffer], { type: file.mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = nameOverride ?? file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
}
